/**
 * Registers the catalog's `find_and_open_patient` action — global scope.
 *
 * Server-side FHIR-backed name resolution: takes a partial / full name,
 * runs a FHIR `Patient?name:contains=…` search scoped to the current
 * organization, and resolves to one of four outcomes:
 *
 *   - 1 match: navigates to /patients/visits/{id} with the resource as
 *     route state (mirroring SelectPatientRightDrawer.handleProceed) and
 *     dispatches the same redux setup (patientObject + setVisitObject +
 *     encounterObject) so the destination route mounts in the right
 *     state. Voice and click share one navigation contract.
 *
 *   - 0 matches: throws a "no_match" Error so the agent can re-prompt
 *     ("I don't have a patient named …; could you spell that?").
 *
 *   - >1 matches: throws an "ambiguous" Error including candidate
 *     names + DOB + gender + id so the agent can ask the right
 *     disambiguating follow-up.
 *
 *   - FHIR error: throws "lookup_failed" so the agent narrates a
 *     graceful retry prompt.
 *
 * Sibling pattern lifted from useCopilotPatientSelectActions.js
 * (drawer-scoped fuzzy match) — same three-failure-mode contract
 * (no_match / ambiguous / ok), generalised to global scope and backed
 * by FHIR rather than a visible-row scan.
 *
 * Click-path equivalent: type a partial name into the patient list's
 * search box, then click the row. Voice compresses both into one
 * utterance and works from any route.
 *
 * Capability matrix (plan §10, locked 2026-05-06 — kept verbatim
 * here so the next reader sees the contract):
 *
 *   #  Voice                            Behaviour
 *   1  "open Spiderman"                 unique match -> navigate
 *   2  "open spider" (partial)          name:contains is partial-friendly
 *   3  "open patient pat-101"           NOT this tool — select_patient
 *   4  (two Spidermen)                  ambiguous w/ candidates (name, DOB, gender, id)
 *   5  "open Wilson" (none)             no_match
 *   6  (no name_hint)                   required-param rejection
 *   7  (FHIR unreachable)               lookup_failed
 *   8  "open Jane" (already on chart)   navigate replaces current page
 */

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { GET_PATIENT_LIST } from "adapters/ehr.service";
import { patientObject } from "redux/actions/PatientActions";
import {
  encounterObject,
  setVisitObject,
} from "redux/actions/EncounterActions";

import { useCopilotAction, copilotBlocked, copilotError } from "./actionRegistry";

const DESCRIPTOR = Object.freeze({
  name: "find_and_open_patient",
  description:
    "Implemented by noki-fe (FHIR Patient name:contains search + navigate).",
  classification: "READ",
  parameters: [
    {
      name: "name_hint",
      type: "string",
      description: "Substring or full name to search for.",
      required: true,
    },
  ],
});

// FHIR page size for the resolution search. Plenty to detect ambiguity
// without dragging the full directory across the wire — if there are
// more than 10 matches, the user's hint is too generic and we ask
// them to narrow it anyway.
const MAX_RESULTS = 10;

export function useCopilotFindAndOpenPatientAction() {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const organisationId = useSelector(
    (state) => state?.auth?.organisationData?.id,
  );
  const descriptor = useMemo(() => DESCRIPTOR, []);

  useCopilotAction(descriptor, async (args) => {
    // eslint-disable-next-line no-console
    console.info("[copilot] find_and_open_patient.invoke", { args });

    const hint =
      typeof args?.name_hint === "string" ? args.name_hint.trim() : "";
    if (!hint) {
      // Typed blocked outcome (not a crash): the model must ask which patient
      // rather than fabricate a name (see the no-invent prompt rule).
      return copilotBlocked(
        "name_required",
        "No patient name was given. Ask the user which patient they mean before searching.",
      );
    }

    // Build the FHIR query. Match the pattern in
    // SelectPatientRightDrawer.getPatient — organization scope first,
    // _count, then name:contains. No URL-encoding to stay consistent
    // with the existing call sites; FHIRSearch handles it.
    let params = `_count=${MAX_RESULTS}&name:contains=${hint}`;
    if (organisationId) {
      params = `organization=${organisationId}&${params}`;
    }

    let entries = [];
    try {
      const res = await GET_PATIENT_LIST("", params);
      entries = Array.isArray(res?.entry) ? res.entry : [];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[copilot] find_and_open_patient.lookup_failed", err);
      return copilotError(
        "lookup_failed",
        `I couldn't reach the patient directory to search for '${hint}'. Tell the user the directory is temporarily unavailable and to try again in a moment.`,
      );
    }

    if (entries.length === 0) {
      // eslint-disable-next-line no-console
      console.warn("[copilot] find_and_open_patient.no_match", { hint });
      return copilotBlocked(
        "no_match",
        `No patient matches '${hint}'. Ask the user to confirm the spelling or give a fuller name.`,
      );
    }

    if (entries.length > 1) {
      // eslint-disable-next-line no-console
      console.warn("[copilot] find_and_open_patient.multiple_matches", {
        hint,
        count: entries.length,
      });
      // Structured (non-error) disambiguation: return the candidate
      // list — name + DOB + gender + id — so the agent reads the names
      // and asks the user which one, then re-queries with a fuller
      // name. (Previously this threw, which the agent could surface as
      // a failure rather than a clarifying question.)
      const candidates = entries.map((entry) => {
        const r = entry?.resource ?? {};
        return {
          patient_id: typeof r.id === "string" ? r.id : null,
          patient_name: r?.name?.[0]?.text || "(unnamed)",
          dob: typeof r.birthDate === "string" ? r.birthDate : null,
          gender: typeof r.gender === "string" ? r.gender : null,
        };
      });
      const names = candidates.map((c) => c.patient_name);
      // Typed BLOCKED outcome (not a plain object): nothing was opened, so this
      // must NOT be narrated as a completed action. It arrives as
      // status="blocked" / multiple_matches (retryable) so the model reads the
      // names and asks which one, then re-queries with a fuller name.
      return copilotBlocked(
        "multiple_matches",
        `Found ${entries.length} patients matching '${hint}': ${names.join(", ")}. `
          + `Read these names to the user and ask which one; when they answer, call `
          + `find_and_open_patient again with the fuller name (use date of birth to `
          + `disambiguate if two truly share a name).`,
        { query: hint, count: entries.length, candidates },
      );
    }

    const [match] = entries;
    const resource = match?.resource ?? {};
    const patientId = resource?.id;
    if (!patientId) {
      return copilotError(
        "lookup_failed",
        `Found a unique match for '${hint}' but the patient record has no id; I can't open the chart. Tell the user to try again.`,
      );
    }
    const display = resource?.name?.[0]?.text || patientId;

    // Same redux + navigate sequence as SelectPatientRightDrawer.handleProceed
    // so the destination chart route mounts with the patient already
    // populated and a clean visit/encounter slate.
    reduxDispatch(patientObject(resource));
    reduxDispatch(setVisitObject({}));
    reduxDispatch(encounterObject({}));
    navigate(`/patients/visits/${patientId}`, {
      state: { rawData: resource },
    });

    // eslint-disable-next-line no-console
    console.info("[copilot] find_and_open_patient.opened", {
      patientId,
      display,
    });

    return {
      patient_id: patientId,
      patient_name: display,
      route: `/patients/visits/${patientId}`,
    };
  });
}
